import { useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import theme from '../theme'
import { DEMO_REDIRECT_URL } from '../utils/auth'
import { login } from '../utils/api'

const fieldStyles = {
  backgroundColor: 'white',
  borderRadius: '2px',
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontSize: '15px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#C9C9D1'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#C9C9D1'
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main
  }
}

function Login({ marketHost, onSuccess }) {
  const [ username, setUsername ] = useState('')
  const [ password, setPassword ] = useState('')
  const [ error, setError ] = useState('')
  const [ busy, setBusy ] = useState(false)

  // the password is checked by the server now, so this is a request rather than a comparison
  const handleSubmit = async (event) => {
    event.preventDefault()
    setBusy(true)

    try {
      const account = await login(marketHost, username, password)
      setError('')

      // locally the demo subdomain does not resolve, so logging in there would land on a dead host
      const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname)
      if (DEMO_REDIRECT_URL && !isLocal) {
        window.location.href = DEMO_REDIRECT_URL
        return
      }

      onSuccess(account)
    } catch (e) {
      setError(e.message || 'Wrong login or password')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  const handleChange = (setter) => (event) => {
    setter(event.target.value)
    if (error) {
      setError('')
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.main
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '340px',
          maxWidth: 'calc(100% - 32px)',
          padding: '32px 28px',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '3px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.35)'
        }}
      >
        <Typography variant="h3" sx={{ color: theme.palette.primary.main, lineHeight: 1.1 }}>
          OddsPulse
        </Typography>
        <Typography
          sx={{
            marginTop: '4px',
            marginBottom: '24px',
            fontSize: '13px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#6E7378'
          }}
        >
          Demo access
        </Typography>

        <TextField
          fullWidth
          autoFocus
          placeholder="Login"
          value={username}
          onChange={handleChange(setUsername)}
          sx={{ ...fieldStyles, marginBottom: '12px' }}
        />
        <TextField
          fullWidth
          type="password"
          placeholder="Password"
          value={password}
          onChange={handleChange(setPassword)}
          sx={fieldStyles}
        />

        <Box
          sx={{
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            color: theme.palette.secondary.main
          }}
        >
          {error}
        </Box>

        <Button
          fullWidth
          type="submit"
          disabled={busy}
          sx={{
            height: '42px',
            fontSize: '15px',
            letterSpacing: '0.06em',
            color: '#ffffff',
            backgroundColor: theme.palette.secondary.main,
            borderRadius: '2px',
            '&:hover': {
              backgroundColor: '#A81819'
            },
            '&.Mui-disabled': { backgroundColor: '#DCDCE2', color: '#9A9EA3' }
          }}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </Box>
    </Box>
  )
}

export default Login
